<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import type { AxiosError } from 'axios';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import CategoryFormFields from '@/js/back-office/components/CategoryFormFields.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { useToastStore } from '@/js/stores/snackbar';
    import {
        useQueryGetBackOfficeTenants,
        useQueryGetCategories,
        useMutationCreateCategory,
        useMutationUpdateCategory,
        useMutationDeleteCategory,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import type { Category } from '@/js/services/api/backOffice/categoriesApi';

    const successDialogStore = useSuccessDialog();
    const confirmationDialogStore = useConfirmationDialog();
    const toastStore = useToastStore();

    const { data: tenantsData } = useQueryGetBackOfficeTenants();

    type Mode = 'list' | 'create' | 'edit';
    type ErrorResponse = { detail?: string; transcriptionCount?: number };

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | null>(null);
    const mode = ref<Mode>('list');
    const editingCategoryId = ref<number | null>(null);

    // Form state. No externalId field - it's only ever assigned by the external-system
    // ingestion path, never editable here.
    const name = ref('');
    const isActive = ref(true);
    const prompt = ref('');
    const selectedParentId = ref<number | null>(null);
    const parentContextName = ref<string | null>(null);

    const { data: categoriesData } = useQueryGetCategories(selectedTenantId);

    const tenantOptions = computed<Option[]>(() =>
        (tenantsData.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );

    // Categories are exactly two levels deep, so grouping is a flat parent -> subcategories
    // map, never a recursive tree.
    const rootCategories = computed(() => (categoriesData.value ?? []).filter((c) => !c.parentId));

    const subcategoriesByParentId = computed(() => {
        const map = new Map<number, Category[]>();
        for (const category of categoriesData.value ?? []) {
            if (!category.parentId) continue;
            const siblings = map.get(category.parentId) ?? [];
            siblings.push(category);
            map.set(category.parentId, siblings);
        }
        return map;
    });

    // The backend unconditionally rejects deleting a root category that still has
    // subcategories - disable the button instead of letting the user hit that error.
    const hasSubcategories = (categoryId: number) =>
        (subcategoriesByParentId.value.get(categoryId) ?? []).length > 0;

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };
    const onTenantClear = () => {
        selectedTenantId.value = '';
    };

    watch(selectedTenantId, () => {
        mode.value = 'list';
    });

    const clearForm = () => {
        name.value = '';
        isActive.value = true;
        prompt.value = '';
        selectedParentId.value = null;
        parentContextName.value = null;
    };

    const startCreate = () => {
        clearForm();
        editingCategoryId.value = null;
        mode.value = 'create';
    };

    const startCreateSubcategory = (parentCategory: Category) => {
        clearForm();
        editingCategoryId.value = null;
        selectedParentId.value = parentCategory.id;
        parentContextName.value = parentCategory.name;
        mode.value = 'create';
    };

    const startEdit = (category: Category) => {
        clearForm();
        editingCategoryId.value = category.id;
        name.value = category.name;
        isActive.value = category.isActive;
        prompt.value = category.prompt;
        // Read-only context only - parent can't be changed once a category exists.
        parentContextName.value = category.parentName ?? null;
        mode.value = 'edit';
    };

    const cancelForm = () => {
        clearForm();
        editingCategoryId.value = null;
        mode.value = 'list';
    };

    const { mutateAsync: createMutation, isPending: isCreating } = useMutationCreateCategory();
    const { mutateAsync: updateMutation, isPending: isUpdating } = useMutationUpdateCategory();
    const { mutateAsync: deleteMutation } = useMutationDeleteCategory();

    const isPending = computed(() => isCreating.value || isUpdating.value);
    const deletingCategoryId = ref<number | null>(null);

    const showDeleteError = (error: unknown) => {
        const detail = (error as AxiosError<ErrorResponse>)?.response?.data?.detail;
        toastStore.addToast('error', detail ?? __('error-response'));
    };

    const deleteCategory = async (categoryId: number) => {
        deletingCategoryId.value = categoryId;
        try {
            await deleteMutation({
                tenantId: selectedTenantId.value,
                id: categoryId,
                confirm: false,
            });
            cancelForm();
        } catch (error) {
            const response = (error as AxiosError<ErrorResponse>)?.response;
            const transcriptionCount = response?.data?.transcriptionCount;

            if (response?.status === 409 && transcriptionCount) {
                const confirmed = await confirmationDialogStore.openDialog({
                    title: __('delete'),
                    message: __('back-office-categories-delete-in-use-confirm', {
                        count: String(transcriptionCount),
                    }),
                });

                if (confirmed) {
                    try {
                        await deleteMutation({
                            tenantId: selectedTenantId.value,
                            id: categoryId,
                            confirm: true,
                        });
                        cancelForm();
                    } catch (retryError) {
                        showDeleteError(retryError);
                    }
                }
            } else {
                showDeleteError(error);
            }
        } finally {
            deletingCategoryId.value = null;
        }
    };

    const submit = async () => {
        if (mode.value === 'edit' && editingCategoryId.value) {
            await updateMutation({
                tenantId: selectedTenantId.value,
                id: editingCategoryId.value,
                data: { name: name.value, isActive: isActive.value, prompt: prompt.value },
            });
        } else {
            await createMutation({
                tenantId: selectedTenantId.value,
                data: {
                    name: name.value,
                    isActive: isActive.value,
                    prompt: prompt.value,
                    parentId: selectedParentId.value || null,
                },
            });
        }

        successDialogStore.openDialog({ message: __('saved') });
        cancelForm();
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-categories') }}</h1>
        </template>
        <template #content>
            <div class="w-lg max-w-full flex flex-col gap-6">
                <AutoComplete
                    v-model="tenantSearch"
                    input-id="categories-tenant"
                    :label="__('back-office-select-customer')"
                    :options="tenantOptions"
                    @select="onTenantSelect"
                    @clear="onTenantClear"
                />

                <template v-if="selectedTenantId">
                    <div class="flex flex-col gap-3">
                        <div
                            v-if="mode === 'create' && !selectedParentId"
                            class="rounded-md border border-gray-light p-4"
                        >
                            <CategoryFormFields
                                v-model:name="name"
                                v-model:prompt="prompt"
                                v-model:is-active="isActive"
                                id-prefix="categories-create"
                                :parent-context-name="null"
                                :is-pending="isPending"
                                :show-delete="false"
                                :is-deleting="false"
                                @submit="submit"
                                @cancel="cancelForm"
                            />
                        </div>
                        <div v-else>
                            <EchoButton type="button" variant="primary" @click="startCreate">
                                {{ __('back-office-categories-add') }}
                            </EchoButton>
                        </div>

                        <div
                            v-for="root in rootCategories"
                            :key="root.id"
                            class="rounded-md border border-gray-light"
                        >
                            <div class="px-4 py-3">
                                <CategoryFormFields
                                    v-if="editingCategoryId === root.id"
                                    v-model:name="name"
                                    v-model:prompt="prompt"
                                    v-model:is-active="isActive"
                                    id-prefix="categories-edit-root"
                                    :parent-context-name="parentContextName"
                                    :is-pending="isPending"
                                    :show-delete="true"
                                    :delete-disabled="hasSubcategories(root.id)"
                                    :is-deleting="deletingCategoryId === root.id"
                                    @submit="submit"
                                    @cancel="cancelForm"
                                    @delete="deleteCategory(root.id)"
                                />
                                <div v-else class="flex items-center justify-between">
                                    <div>
                                        <p class="text-body-bold">
                                            {{ root.name }}
                                            <span v-if="!root.isActive" class="text-gray-medium">
                                                ({{ __('inactive') }})
                                            </span>
                                        </p>
                                        <p
                                            v-if="root.externalId"
                                            class="text-body text-gray-medium"
                                        >
                                            {{ __('external-id') }}: {{ root.externalId }}
                                        </p>
                                    </div>
                                    <div class="flex gap-2">
                                        <EchoButton
                                            type="button"
                                            variant="secondary"
                                            @click="startCreateSubcategory(root)"
                                        >
                                            {{ __('back-office-categories-add-sub') }}
                                        </EchoButton>
                                        <EchoButton
                                            type="button"
                                            variant="secondary"
                                            @click="startEdit(root)"
                                        >
                                            {{ __('edit') }}
                                        </EchoButton>
                                        <EchoButton
                                            type="button"
                                            variant="secondary"
                                            :disabled="hasSubcategories(root.id)"
                                            :title="
                                                hasSubcategories(root.id)
                                                    ? __('back-office-categories-delete-blocked-has-subcategories')
                                                    : undefined
                                            "
                                            :loading="deletingCategoryId === root.id"
                                            @click="deleteCategory(root.id)"
                                        >
                                            {{ __('delete') }}
                                        </EchoButton>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="
                                    (subcategoriesByParentId.get(root.id) ?? []).length > 0 ||
                                    (mode === 'create' && selectedParentId === root.id)
                                "
                                class="flex flex-col gap-3 border-t border-gray-light px-4 py-3 pl-8"
                            >
                                <div
                                    v-for="sub in subcategoriesByParentId.get(root.id)"
                                    :key="sub.id"
                                    :class="{ 'rounded-md border border-gray-light p-3': editingCategoryId === sub.id }"
                                >
                                    <CategoryFormFields
                                        v-if="editingCategoryId === sub.id"
                                        v-model:name="name"
                                        v-model:prompt="prompt"
                                        v-model:is-active="isActive"
                                        id-prefix="categories-edit-sub"
                                        :parent-context-name="parentContextName"
                                        :is-pending="isPending"
                                        :show-delete="true"
                                        :is-deleting="deletingCategoryId === sub.id"
                                        @submit="submit"
                                        @cancel="cancelForm"
                                        @delete="deleteCategory(sub.id)"
                                    />
                                    <div v-else class="flex items-center justify-between">
                                        <div>
                                            <p class="text-body-bold">
                                                {{ sub.name }}
                                                <span v-if="!sub.isActive" class="text-gray-medium">
                                                    ({{ __('inactive') }})
                                                </span>
                                            </p>
                                            <p
                                                v-if="sub.externalId"
                                                class="text-body text-gray-medium"
                                            >
                                                {{ __('external-id') }}: {{ sub.externalId }}
                                            </p>
                                        </div>
                                        <div class="flex gap-2">
                                            <EchoButton
                                                type="button"
                                                variant="secondary"
                                                @click="startEdit(sub)"
                                            >
                                                {{ __('edit') }}
                                            </EchoButton>
                                            <EchoButton
                                                type="button"
                                                variant="secondary"
                                                :loading="deletingCategoryId === sub.id"
                                                @click="deleteCategory(sub.id)"
                                            >
                                                {{ __('delete') }}
                                            </EchoButton>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    v-if="mode === 'create' && selectedParentId === root.id"
                                    class="rounded-md border border-gray-light p-3"
                                >
                                    <CategoryFormFields
                                        v-model:name="name"
                                        v-model:prompt="prompt"
                                        v-model:is-active="isActive"
                                        id-prefix="categories-create-sub"
                                        :parent-context-name="parentContextName"
                                        :is-pending="isPending"
                                        :show-delete="false"
                                        :is-deleting="false"
                                        @submit="submit"
                                        @cancel="cancelForm"
                                    />
                                </div>
                            </div>
                        </div>
                        <p
                            v-if="categoriesData && rootCategories.length === 0"
                            class="text-body text-gray-medium"
                        >
                            {{ __('back-office-categories-none') }}
                        </p>
                    </div>
                </template>
            </div>
        </template>
    </SettingsPageLayout>
</template>
